import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ContractWithDetails } from '../types/contracts';
import { ContractViewer } from '../components/contracts/ContractViewer';
import { SignaturePad } from '../components/contracts/SignaturePad';
import { FileSignature, ShieldCheck, AlertCircle, CheckCircle2, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo } from '../components/Logo';
import { v4 as uuidv4 } from 'uuid';

export function ContractSigning() {
  const { token } = useParams();
  const [contract, setContract] = useState<ContractWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Signing state
  const [signerType, setSignerType] = useState<'buyer' | 'seller'>('buyer');
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  useEffect(() => {
    if (token) {
      loadContract();
    } else {
      setError('No signing token provided.');
      setIsLoading(false);
    }
  }, [token]);

  const loadContract = async () => {
    try {
      const { data: c, error: cErr } = await supabase!
        .from('contracts')
        .select('*')
        .eq('signing_token', token)
        .single();

      if (cErr || !c) throw new Error('Invalid or expired signing link.');

      if (c.expires_at && new Date(c.expires_at) < new Date()) {
        throw new Error('This signing link has expired.');
      }

      // Mark as viewed
      if (c.status === 'sent') {
        const viewedLog = [...(c.activity_log || []), {
          id: uuidv4(),
          action: 'viewed',
          actor: 'Signer',
          timestamp: new Date().toISOString(),
        }];
        await supabase!.from('contracts')
          .update({ status: 'viewed', activity_log: viewedLog })
          .eq('id', c.id);
      }

      const { data: v } = await supabase!
        .from('contract_versions')
        .select('*')
        .eq('contract_id', c.id)
        .order('version_number', { ascending: false })
        .limit(1);

      const { data: s } = await supabase!
        .from('contract_signatures')
        .select('*')
        .eq('contract_id', c.id);

      setContract({
        ...c,
        versions: v || [],
        signatures: s || [],
        activity_log: c.activity_log || [],
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getClientIp = async (): Promise<string | null> => {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      return data.ip || null;
    } catch {
      return null;
    }
  };

  const handleSubmitSignature = async () => {
    if (!contract || !signatureData || !signerName.trim()) {
      toast.error('Please provide your name and signature.');
      return;
    }
    if (!consentGiven) {
      toast.error('Please confirm your electronic consent to proceed.');
      return;
    }

    setIsSubmitting(true);
    try {
      const ipAddress = await getClientIp();
      const userAgent = navigator.userAgent;
      const transactionId = uuidv4();

      const { error: sErr } = await supabase!.from('contract_signatures').insert({
        contract_id: contract.id,
        signer_type: signerType,
        signer_name: signerName.trim(),
        signature_data: signatureData,
        ip_address: ipAddress,
        user_agent: userAgent,
        consent_given: consentGiven,
        transaction_id: transactionId,
      });

      if (sErr) throw sErr;

      // Check if all parties have signed → execute
      const existingSignatures = contract.signatures || [];
      const hasOtherSignature = existingSignatures.some(s => s.signer_type !== signerType);

      const newStatus = (hasOtherSignature || existingSignatures.length > 0) ? 'executed' : 'accepted';
      const activityLog = [...(contract.activity_log || []), {
        id: uuidv4(),
        action: newStatus === 'executed' ? 'executed' : 'signed',
        actor: signerName.trim(),
        timestamp: new Date().toISOString(),
        note: `${signerType} signed the contract`,
      }];

      await supabase!.from('contracts').update({
        status: newStatus,
        activity_log: activityLog,
      }).eq('id', contract.id);

      setHasSigned(true);
      toast.success('Signature submitted successfully!');
    } catch (err: any) {
      toast.error(`Error saving signature: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-[var(--t-text-muted)]">
          <Loader2 size={32} className="animate-spin text-[var(--t-primary)]" />
          <p className="text-sm font-medium">Loading document securely...</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] flex flex-col items-center justify-center p-4">
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-[var(--t-error)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={32} className="text-[var(--t-error)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--t-text)] mb-2">Access Denied</h2>
          <p className="text-[var(--t-text-muted)] mb-8">{error}</p>
          <a href="/" className="text-[var(--t-primary)] hover:underline text-sm font-medium">Return to Homepage</a>
        </div>
      </div>
    );
  }

  const latestContent = contract.versions[0]?.content;

  if (hasSigned) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] flex flex-col items-center justify-center p-4">
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-[var(--t-success-dim)] rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full bg-[var(--t-success)]/20 animate-ping" />
            <CheckCircle2 size={40} className="text-[var(--t-success)] relative z-10" />
          </div>
          <h2 className="text-2xl font-black text-[var(--t-text)] mb-2">You're All Set!</h2>
          <p className="text-[var(--t-text-muted)] mb-2">
            Your signature has been securely recorded.
          </p>
          <p className="text-sm text-[var(--t-text-muted)] mb-8">
            You will receive a copy once all parties have signed.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-[var(--t-text-muted)]">
            <ShieldCheck size={14} className="text-[var(--t-success)]" /> Secured by WholeScale OS
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--t-bg)] flex flex-col font-inter">
      {/* Header */}
      <header className="bg-[var(--t-surface)] border-b border-[var(--t-border)] px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Logo />
          <div className="h-6 w-px bg-[var(--t-border)] mx-2" />
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--t-text)]">
            <ShieldCheck size={16} className="text-[var(--t-success)]" />
            Secure Signing Portal
          </div>
        </div>
        <div className="text-xs text-[var(--t-text-muted)] hidden sm:block">{contract.title}</div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl w-full mx-auto p-4 md:p-8 flex flex-col gap-8">

        {/* Step 1: Review */}
        <div>
          <h2 className="text-lg font-bold text-[var(--t-text)] mb-4 flex items-center gap-2">
            <FileText size={20} className="text-[var(--t-primary)]" />
            1. Review Document
          </h2>
          {latestContent ? (
            <ContractViewer content={latestContent} viewerRole={signerType} />
          ) : (
            <div className="p-8 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl text-center text-[var(--t-text-muted)]">
              Document content not available.
            </div>
          )}
        </div>

        {/* Step 2: Sign */}
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg font-bold text-[var(--t-text)] mb-6 flex items-center gap-2">
            <FileSignature size={20} className="text-[var(--t-primary)]" />
            2. Sign &amp; Complete
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col gap-5">
              <div>
                <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-2">I am signing as:</label>
                <div className="flex gap-2">
                  {(['buyer', 'seller'] as const).map(role => (
                    <button
                      key={role}
                      onClick={() => setSignerType(role)}
                      className={`flex-1 py-2 px-4 rounded-xl text-sm font-semibold transition-colors border capitalize ${
                        signerType === role
                          ? 'bg-[var(--t-primary-dim)] text-[var(--t-primary)] border-[var(--t-primary)]'
                          : 'bg-[var(--t-bg)] text-[var(--t-text-muted)] border-[var(--t-border)] hover:border-[var(--t-primary)]'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-2">Full Legal Name</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="e.g. John Doe"
                  className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
                />
              </div>

              {/* Consent */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${consentGiven ? 'bg-[var(--t-primary)] border-[var(--t-primary)]' : 'bg-[var(--t-bg)] border-[var(--t-border)] group-hover:border-[var(--t-primary)]'}`}>
                    {consentGiven && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                </div>
                <span className="text-xs text-[var(--t-text-muted)] leading-relaxed">
                  I agree to conduct this transaction electronically and confirm that my electronic signature
                  is legally binding under the ESIGN Act and UETA. I have reviewed and understand all terms
                  in this document.
                </span>
              </label>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-2">Draw your signature:</label>
              <SignaturePad onSignatureChange={setSignatureData} />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[var(--t-border)] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-[var(--t-text-muted)]">
              <ShieldCheck size={14} className="text-[var(--t-success)]" />
              <span>256-bit encryption · IP logged · Timestamp recorded</span>
            </div>
            <button
              onClick={handleSubmitSignature}
              disabled={isSubmitting || !signatureData || !signerName.trim() || !consentGiven}
              className="w-full sm:w-auto px-8 py-3 bg-[var(--t-primary)] text-white font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[var(--t-primary-dim)] shrink-0 flex items-center gap-2 justify-center"
            >
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" /> Submitting...</>
              ) : (
                <><FileSignature size={16} /> Submit Signature</>
              )}
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-[var(--t-text-muted)]">
        Secured by WholeScale OS · Electronic signatures are legally binding under ESIGN &amp; UETA
      </footer>
    </div>
  );
}
